import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Observable } from 'rxjs';
import { DevExButtonComponent } from '../../../devExtreme/src/devExButton/devExButton.component';
import { MatButtonComponent } from '../../../material/src/matButton/matButton.component';
import { ComponentStyle, IAbcComponent } from '../abcComponent.model';
import { AbcComponentService } from '../abcComponent.service';
import { badgePosition, ButtonColor, ButtonStyle, ButtonVariant, IButton, Icon, TypeStyle } from './abcButton';

@Component({
  selector: 'abcButton',
  template: '<ng-template #abcButton></ng-template>',
})
export class AbcButtonComponent implements IAbcComponent, OnInit, AfterViewInit, OnDestroy {
  @Input('id') attrId: string;
  @Input() variant: string;
  @Input() buttonBackgroundColor: string;
  @Input() iconColor: string;
  @Input() iconBackgroundColor: string;
  @Input() image: string;
  @Output() onClick = new EventEmitter();
  private _color: ButtonColor;
  private _stylingMode: ButtonVariant;
  private _value: string;
  private _width: string;
  private _height: string;
  private _icon: Icon | string;
  private _disabled: boolean;
  private _focusStateEnabled: boolean;
  private _hoverStateEnabled: boolean;
  private _visible: boolean;
  private _hint: string;
  private _class: string;
  private _type: TypeStyle;
  private _rtlEnabled: boolean;
  private _style: ComponentStyle;
  private _badge: number;
  private _badgePosition: badgePosition;
  private _badgeColor: string;

  @Input()
  get badge(): number {
    return this._badge;
  }
  set badge(val: number) {
    this._badge = val;
    if (this.componentRef) {
      this.componentRef.badge = val;
    }
  }

  @Input()
  get badgePosition(): badgePosition {
    if (!this._badgePosition) {
      this._badgePosition = badgePosition.aboveAfter;
    }
    return this._badgePosition;
  }
  set badgePosition(val: badgePosition) {
    this._badgePosition = val;
    if (this.componentRef) {
      this.componentRef.badgePosition = val;
    }
  }
  @Input()
  get badgeColor(): string {
    return this._badgeColor;
  }
  set badgeColor(val: string) {
    this._badgeColor = val;
    if (this.componentRef) {
      this.componentRef.badgeColor = val;
    }
  }

  @Input()
  get icon(): Icon | string {
    return this._icon;
  }
  set icon(val: Icon | string) {
    this._icon = val;
    if (this.componentRef) {
      this.componentRef.icon = val;
    }
  }

  @Input()
  get hint(): string {
    return this._hint;
  }
  set hint(val: string) {
    this._hint = val;
    if (this.componentRef) {
      this.componentRef.hint = val;
    }
  }

  @Input()
  get class(): string {
    return this._class;
  }
  set class(val: string) {
    this._class = val;
    if (this.componentRef) {
      this.componentRef.class = val;
    }
  }

  @Input()
  get type(): TypeStyle {
    return this._type;
  }
  set type(val: TypeStyle) {
    this._type = val;
    if (this.componentRef) {
      this.componentRef.type = val;
    }
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(val: boolean) {
    this._disabled = val;
    if (this.componentRef) {
      this.componentRef.disabled = val;
    }
  }

  @Input()
  get rtlEnabled(): boolean {
    return this._rtlEnabled;
  }
  set rtlEnabled(val: boolean) {
    this._rtlEnabled = val;
    if (this.componentRef) {
      this.componentRef.rtlEnabled = val;
    }
  }

  @Input()
  get hoverStateEnabled(): boolean {
    return this._hoverStateEnabled;
  }
  set hoverStateEnabled(val: boolean) {
    this._hoverStateEnabled = val;
    if (this.componentRef) {
      this.componentRef.hoverStateEnabled = val;
    }
  }

  @Input()
  get focusStateEnabled(): boolean {
    return this._focusStateEnabled;
  }
  set focusStateEnabled(val: boolean) {
    this._focusStateEnabled = val;
    if (this.componentRef) {
      this.componentRef.focusStateEnabled = val;
    }
  }

  @Input()
  get visible(): boolean {
    return this._visible;
  }
  set visible(val: boolean) {
    this._visible = val;
    if (this.componentRef) {
      this.componentRef.visible = val;
    }
  }

  @Input()
  get style() {
    return this._style;
  }
  set style(data: ComponentStyle) {
    this._style = data;

    if (this.componentRef) {
      this.viewContainerRef.clear();
      this.createComponentInstance().subscribe((component) => {
        this.eventListener.forEach((element) => {
          component.addEventListener(element.type, element.listener);
        });
      });
    }
  }

  @Input()
  get stylingMode(): ButtonVariant {
    return this._stylingMode;
  }
  set stylingMode(val: ButtonVariant) {
    this._stylingMode = val;
    if (this.componentRef) {
      this.componentRef.stylingMode = val;
    }
  }

  @Input()
  get color(): ButtonColor {
    return this._color;
  }
  set color(val: ButtonColor) {
    this._color = val;
    if (this.componentRef) {
      this.componentRef.color = val;
    }
  }

  @Input()
  get height(): string {
    return this._height;
  }
  set height(val: string) {
    this._height = val;
    if (this.componentRef) {
      this.componentRef.height = val;
    }
  }

  @Input()
  get width(): string {
    return this._width;
  }
  set width(val: string) {
    this._width = val;
    if (this.componentRef) {
      this.componentRef.width = val;
    }
  }

  @Input()
  get value(): string {
    return this._value;
  }
  set value(val: string) {
    this._value = val;
    if (this.componentRef) {
      this.componentRef.value = val;
    }
  }

  @ViewChild('abcButton', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  private componentRef;
  get id(): string {
    return this.componentRef.id;
  }
  htmlAttributes: IButton;
  private eventListener = [];

  constructor(private abcComponentService: AbcComponentService) {
    this.style = ComponentStyle.Material;
  }

  ngOnInit(): void {
    this.setButtonsAttributesIfAvailable();
    this.createComponentInstance().subscribe((component) => {
      this.eventListener.forEach((element) => {
        component.addEventListener(element.type, element.listener);
      });
    });
  }

  ngAfterViewInit(): void {
    this.abcComponentService.addInstance(this);
  }

  ngOnDestroy(): void {
    this.eventListener = [];
    this.abcComponentService.removeInstance(this);
  }

  createComponentInstance(): Observable<any> {
    const observable = new Observable((observer) => {
      switch (this.style) {
        case ComponentStyle.Material: {
          this.componentRef = this.abcComponentService.loadComponent(
            MatButtonComponent,
            this.viewContainerRef
          ).componentRef.instance;
          break;
        }
        case ComponentStyle.DevExpress: {
          this.componentRef = this.abcComponentService.loadComponent(
            DevExButtonComponent,
            this.viewContainerRef
          ).componentRef.instance;
          //do specific stuff for DevExpress
          this.componentRef.onClick.subscribe((e) => {
            this.onClickButton(e);
          });
          break;
        }
      }

      if (this.htmlAttributes.id) this.componentRef.id = this.htmlAttributes.id;
      if (this.htmlAttributes.value) this.componentRef.value = this.htmlAttributes.value;
      if (this.htmlAttributes.disabled) this.componentRef.disabled = this.htmlAttributes.disabled;
      if (this.htmlAttributes.color) this.componentRef.color = this.htmlAttributes.color;
      if (this.htmlAttributes.buttonBackgroundColor)
        this.componentRef.buttonBackgroundColor = this.htmlAttributes.buttonBackgroundColor;
      if (this.htmlAttributes.class) this.componentRef.class = this.htmlAttributes.class;
      if (this.htmlAttributes.icon) this.componentRef.icon = this.htmlAttributes.icon;
      if (this.htmlAttributes.badge) this.componentRef.badge = this.htmlAttributes.badge;
      if (this.htmlAttributes.badgePosition) this.componentRef.badgePosition = this.htmlAttributes.badgePosition;
      if (this.htmlAttributes.badgeColor) this.componentRef.badgeColor = this.htmlAttributes.badgeColor;

      if (this.htmlAttributes.hint) this.componentRef.hint = this.htmlAttributes.hint;
      if (this.htmlAttributes.iconColor) this.componentRef.iconColor = this.htmlAttributes.iconColor;
      if (this.htmlAttributes.iconBackgroundColor)
        this.componentRef.iconBackgroundColor = this.htmlAttributes.iconBackgroundColor;
      if (this.htmlAttributes.variant) this.componentRef.variant = this.htmlAttributes.variant;
      if (this.htmlAttributes.stylingMode) this.componentRef.stylingMode = this.htmlAttributes.stylingMode;
      if (this.htmlAttributes.image) this.componentRef.image = this.htmlAttributes.image;
      this.componentRef.visible = this.htmlAttributes.visible ? this.htmlAttributes : true;
      this.componentRef.rtlEnabled = this.htmlAttributes.rtlEnabled ? this.htmlAttributes : false;
      this.componentRef.setButtonStyle(this.htmlAttributes.setButtonStyle());
      observer.next(this.componentRef);
    });

    return observable;
  }

  setCustomDataAttributes(dataAttr: Object): void {
    this.componentRef.setCustomDataAttributes(dataAttr);
  }

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void {
    this.eventListener.push({ type, listener });
  }

  // If the component is created inside HTML
  setButtonsAttributesIfAvailable(): void {
    if (this.attrId) {
      const style: ButtonStyle = {
        height: this._height ? this._height : '40px',
        width: this._width ? this._width : '40px',
      };
      this.htmlAttributes = {
        id: this.attrId,
        value: this._value ? this._value : '',
        image: this.image,
        buttonBackgroundColor: this.buttonBackgroundColor,
        color: this._color,
        variant: this.variant,
        icon: this._icon,
        badge: this.badge,
        badgePosition: this.badgePosition,
        badgeColor: this.badgeColor,
        hint: this._hint,
        class: this._class,
        iconColor: this.iconColor,
        stylingMode: this._stylingMode,
        visible: this.visible,
        focusStateEnabled: this.focusStateEnabled,
        hoverStateEnabled: this.hoverStateEnabled,
        rtlEnabled: this.rtlEnabled,
        type: this.type,
        disabled: this.disabled,
        iconBackgroundColor: this.iconBackgroundColor,
        setButtonStyle: (_inputStyle) => (_inputStyle = style),
        setCustomDataAttributes: () => {
          // To do...
        },
      };
    }
  }
  private onClickButton(e): void {
    this.onClick.emit(e);
  }
}
